/**
 * wholesaleOrder.js
 *
 * @version 1.0.0
 */

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */
const ELEM = {
	form: '.wholesale-order',
	table: '.wholesale-order__table',
	summary: '.wholesale-order__summary',
	summaryBody: '.wholesale-order__summary-body',
	summmaryFooter: '.wholesale-order__summary-footer',
	subtotal: '.wholesale-order__subtotal',
	headerSubtotal: '.wholesale-order__header-subtotal',
	disclaimer: '.wholesale-order__disclaimer',
	submit: '.wholesale-order__btn',
	message: '.wholesale-order__message',
	orderDetails: '#WholesaleOrderDetails',
	count: '.wholesale-order__count',

	filter: '#productFilter',
	clearFilter: '#clearFilter',
	resetOrder: '#resetOrder',

	qtyWrap: '.js-qty',
	qtyBtn: '.js-qty__btn',
	qtyInput: '.js-qty__input',
	lineRemove: '.wholesale-line__remove',

	/* categories that legally require a licence */
	alcoholCheckbox: '.form--checkbox .form__checkbox, .field--alcohol .form__checkbox',
	alcoholBlocks: '.field--alcohol',
	alcoholInput: '[name="contact[liquor_license_number]"]',
	alcCategories: ['beer', 'spirits', 'liqueurs'],
};

/* ----------------------------------------------------------------------- */
/*  Vodka Promo State                                                      */
/* ----------------------------------------------------------------------- */
const vodkaPromoState = {
	vodkaQuantity: 0,
	selectedCordials: {}, // { productId: quantity }
	cordialProducts: []
};

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */
let cart = Object.create(null); // { [variantId]: { qty, title, img, price, pkgSingle, pkgPlural } }

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const moneyToNumber = (s) => Number(s.replace(/[^0-9.-]+/g, ''));
const formatMoney = (n) => `$${n.toFixed(2)}`;
const calcSubtotal = () => Object.values(cart).reduce((a, { qty, price }) => a + qty * price, 0);

const hide = (el) => el && el.classList.add('hide');
const show = (el) => el && el.classList.remove('hide');

/**
+ * Show / hide liquor-licence field when the "ordering alcohol?" checkbox is toggled.
+ * - If checked   → reveal `.field--alcohol`, enable & require its input
+ * - If unchecked → hide block, clear value, disable & remove `required`
+ */
function toggleAlcoholFields(form) {
	const chk = form.querySelector(ELEM.alcoholCheckbox);
	const input = form.querySelector(ELEM.alcoholInput);
	if (!chk || !input) return;

	const showFields = chk.checked;

	/* 1. show / hide every alcohol block */
	form.querySelectorAll(ELEM.alcoholBlocks).forEach((blk) =>
		blk.classList.toggle('hide', !showFields)
	);

	/* 2. update the licence input itself */
	if (showFields) {
		input.hidden = false;
		input.disabled = false;
		input.required = true;
	} else {
		input.value = '';
		input.hidden = true;
		input.disabled = true;
		input.required = false;
	}
}

/**
 * Auto-check the "ordering alcohol?" box if the cart contains Beer, Spirits, or Liqueurs,
 * and keep the licence field in sync.
 */
function enforceAlcoholRequirement(form) {
	const chk = form.querySelector(ELEM.alcoholCheckbox);
	if (!chk) return;

	const needsLicence = Object.values(cart)
		.some((it) => ELEM.alcCategories.includes((it.category || '').toLowerCase()));

	/* Only flip the checkbox if its state is wrong — avoids infinite loops */
	if (chk.checked !== needsLicence) {
		chk.checked = needsLicence;
		toggleAlcoholFields(form);
	}
}


function applyFilter(table, filterVal = 'all') {
	$$('#productFilter option').forEach((o) => o.selected = o.value === filterVal);

	$$('tbody tr', table).forEach((tr) => {
		const cat = (tr.dataset.category || '').toLowerCase();
		const showRow = filterVal === 'all' || cat === filterVal;
		tr.classList.toggle('hide', !showRow);
	});
}

function updateActiveRows(table) {
	$$('tbody tr', table).forEach((tr) => {
		const id = $(ELEM.qtyInput, tr)?.dataset.variantId;
		tr.classList.toggle('is-active', !!cart[id]);
	});
}

/* ----------------------------------------------------------------------- */
/*  Vodka Promo Functions                                                  */
/* ----------------------------------------------------------------------- */

function getCordialProducts() {
	const cordialRows = document.querySelectorAll('tr[data-category="cordials"]');
	const cordials = [];

	cordialRows.forEach(row => {
		const input = row.querySelector('.js-qty__input');
		if (!input) return;

		const productHandle = input.dataset.productHandle;
		// Exclude green-melon-cordial (coming soon)
		if (productHandle === 'green-melon-cordial') return;

		const img = row.querySelector('img');
		const titleDiv = row.querySelector('td:first-child > div');
		const title = titleDiv ? titleDiv.childNodes[0].textContent.trim() : '';

		cordials.push({
			id: input.dataset.variantId,
			handle: productHandle,
			title: title,
			image: img ? img.src : ''
		});
	});

	return cordials;
}

function openCordialModal(quantity) {
	const modal = document.getElementById('cordialModal');
	const grid = document.getElementById('cordialGrid');
	const countSpan = document.getElementById('cordialCount');

	if (!modal || !grid) return;

	countSpan.textContent = quantity;
	grid.innerHTML = '';

	// Get cordial products if not already cached
	if (vodkaPromoState.cordialProducts.length === 0) {
		vodkaPromoState.cordialProducts = getCordialProducts();
	}

	// Create cordial selection cards
	vodkaPromoState.cordialProducts.forEach(cordial => {
		const card = document.createElement('div');
		card.className = 'cordial-card';
		card.dataset.cordialId = cordial.id;

		const currentQty = vodkaPromoState.selectedCordials[cordial.id] || 0;
		if (currentQty > 0) {
			card.classList.add('cordial-card--selected');
		}

		card.innerHTML = `
			<img src="${cordial.image}" alt="${cordial.title}" class="cordial-card__img">
			<h4 class="cordial-card__title">${cordial.title}</h4>
			<div class="qty js-qty cordial-qty">
				<button class="qty__btn js-qty__btn js-cordial-minus" type="button" aria-label="Decrease">
					<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentcolor" d="M3 10a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 10Z"/></svg>
				</button>
				<input class="qty__input js-cordial-input" type="number" min="0" max="${quantity}" value="${currentQty}" data-cordial-id="${cordial.id}" readonly>
				<button class="qty__btn js-qty__btn js-cordial-plus" type="button" aria-label="Increase">
					<svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path fill="currentcolor" d="M10.75 3.75a.75.75 0 0 0-1.5 0v5.5h-5.5a.75.75 0 0 0 0 1.5h5.5v5.5a.75.75 0 0 0 1.5 0v-5.5h5.5a.75.75 0 0 0 0-1.5h-5.5v-5.5Z"/></svg>
				</button>
			</div>
		`;

		grid.appendChild(card);
	});

	modal.classList.remove('hide');
	document.body.style.overflow = 'hidden';
}

function closeCordialModal() {
	const modal = document.getElementById('cordialModal');
	if (!modal) return;

	modal.classList.add('hide');
	document.body.style.overflow = '';
}

function updateCordialQuantity(cordialId, change, maxQty) {
	const input = document.querySelector(`.js-cordial-input[data-cordial-id="${cordialId}"]`);
	if (!input) return;

	let currentValue = parseInt(input.value) || 0;
	let newValue = currentValue + change;

	// Calculate total selected
	const totalSelected = Object.values(vodkaPromoState.selectedCordials).reduce((sum, qty) => sum + qty, 0);
	const otherSelectedTotal = totalSelected - currentValue;

	// Ensure we don't exceed max quantity
	if (newValue < 0) newValue = 0;
	if (newValue > maxQty) newValue = maxQty;
	if (otherSelectedTotal + newValue > maxQty) newValue = maxQty - otherSelectedTotal;

	input.value = newValue;
	vodkaPromoState.selectedCordials[cordialId] = newValue;

	// Update card appearance
	const card = input.closest('.cordial-card');
	if (card) {
		card.classList.toggle('cordial-card--selected', newValue > 0);
	}
}

function confirmCordialSelection(dom) {
	// Store free cordial selection in hidden field
	const freeCordialField = document.getElementById('FreeCordialDetails');
	if (freeCordialField) {
		const cordialDetails = [];
		for (const [cordialId, qty] of Object.entries(vodkaPromoState.selectedCordials)) {
			if (qty > 0) {
				const cordial = vodkaPromoState.cordialProducts.find(c => c.id === cordialId);
				if (cordial) {
					cordialDetails.push(`${cordial.title} x${qty} (FREE with vodka promo)`);
				}
			}
		}
		freeCordialField.value = cordialDetails.join('\n');
	}

	closeCordialModal();
	renderSummary(dom);
}

function renderPromoSection(dom) {
	const promoSection = document.getElementById('promoSection');
	if (!promoSection) return;

	// Only show if vodka is in cart
	if (vodkaPromoState.vodkaQuantity === 0) {
		promoSection.classList.add('hide');
		promoSection.innerHTML = '';
		return;
	}

	// Show the promo section
	promoSection.classList.remove('hide');

	const totalFreeCordials = Object.values(vodkaPromoState.selectedCordials).reduce((sum, qty) => sum + qty, 0);
	const remaining = vodkaPromoState.vodkaQuantity - totalFreeCordials;

	// Clear and rebuild content
	promoSection.innerHTML = '';

	// Add header
	const header = document.createElement('h5');
	header.textContent = 'Free Cordials:';
	promoSection.appendChild(header);

	// Show selected cordials if any
	if (totalFreeCordials > 0) {
		for (const [cordialId, qty] of Object.entries(vodkaPromoState.selectedCordials)) {
			if (qty > 0) {
				const cordial = vodkaPromoState.cordialProducts.find(c => c.id === cordialId);
				if (cordial) {
					const lineItem = document.createElement('div');
					lineItem.className = 'wholesale-line';
					lineItem.innerHTML = `
						<div style="display: flex; align-items: center;">
							<img src="${cordial.image}" alt="${cordial.title}" class="wholesale-line__img">
							<div class="wholesale-line__title">
								<span>${cordial.title}</span>
								<small>${qty} box${qty > 1 ? 'es' : ''} (FREE)</small>
							</div>
						</div>
						<div class="wholesale-line__price">
							<strong>$0.00</strong>
						</div>
					`;
					promoSection.appendChild(lineItem);
				}
			}
		}
	}

	// Add button to select/change cordials
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'btn btn--outline btn--full select-cordials-btn';
	button.style.cssText = 'margin-top: 0.75rem; font-size: 0.75rem;';

	if (totalFreeCordials === 0) {
		button.textContent = `Select Your ${vodkaPromoState.vodkaQuantity} Free Cordial${vodkaPromoState.vodkaQuantity > 1 ? 's' : ''}`;
	} else if (remaining > 0) {
		button.textContent = `Select ${remaining} More Cordial${remaining > 1 ? 's' : ''} (${totalFreeCordials}/${vodkaPromoState.vodkaQuantity} selected)`;
	} else {
		button.textContent = `Change Cordial Selection (${totalFreeCordials}/${vodkaPromoState.vodkaQuantity} selected)`;
	}

	button.addEventListener('click', () => {
		openCordialModal(vodkaPromoState.vodkaQuantity);
	});

	promoSection.appendChild(button);
}

function updateVodkaPromoState(dom) {
	// Check if pure-spring-vodka is in the cart
	let vodkaQty = 0;

	for (const [id, item] of Object.entries(cart)) {
		const input = $(`${ELEM.qtyInput}[data-variant-id="${id}"]`, dom.table);
		if (input && input.dataset.productHandle === 'pure-spring-vodka') {
			vodkaQty = item.qty;
			break;
		}
	}

	// Update vodka quantity
	const oldQty = vodkaPromoState.vodkaQuantity;
	vodkaPromoState.vodkaQuantity = vodkaQty;

	// Clean up selections if quantity decreased
	if (vodkaQty < oldQty) {
		const totalSelected = Object.values(vodkaPromoState.selectedCordials).reduce((sum, qty) => sum + qty, 0);

		if (totalSelected > vodkaQty) {
			// Proportionally reduce selections
			const ratio = vodkaQty / totalSelected;
			for (const cordialId in vodkaPromoState.selectedCordials) {
				vodkaPromoState.selectedCordials[cordialId] = Math.floor(vodkaPromoState.selectedCordials[cordialId] * ratio);
			}
		}
	}

	// Clear if no vodka
	if (vodkaQty === 0) {
		vodkaPromoState.selectedCordials = {};
	}
}

/**
 * Toggle the "more items" fades/hint depending on whether the summary body
 * (the scroll container) has overflowing content above/below the fold.
 */
function updateScrollHints(dom) {
	const body = dom.summaryBody;
	if (!body || !$('.wholesale-order__scroll-fade', body)) return;

	const moreBelow = body.scrollHeight - body.clientHeight - body.scrollTop > 4;
	const moreAbove = body.scrollTop > 4;

	body.classList.toggle('has-more-below', moreBelow);
	body.classList.toggle('has-more-above', moreAbove);
}

function renderSummary(dom) {
	/* wipe old lines */
	$$('.wholesale-line', dom.summaryBody).forEach((n) => n.remove());

	/* build new lines */
	Object.entries(cart).forEach(([id, item]) => {
		const { title, img, qty, price, wasPrice, pkgSingle, pkgPlural } = item;

		const wrap = document.createElement('div');
		wrap.className = 'wholesale-line';
		wrap.dataset.variantId = id;
		dom.summaryBody.appendChild(wrap);

		/* image */
		if (img) {
			const i = new Image();
			i.className = 'wholesale-line__img';
			i.src = img; i.alt = title; i.width = 80; i.height = 80;
			wrap.appendChild(i);
		}

		/* title + qty */
		wrap.insertAdjacentHTML(
			'beforeend',
			`<div class="wholesale-line__title">
				<span>${title}</span>
				<small>Quantity: ${qty} ${qty === 1 ? pkgSingle : pkgPlural}</small>
				${wasPrice ? '<small class="promo-text promo-text--flash">Flash sale 25% off</small>' : ''}
				<button class="wholesale-line__remove" aria-label="Remove ${title}">Remove</button>
			</div>`
		);

		/* price (struck-through original when on flash sale) */
		wrap.insertAdjacentHTML(
			'beforeend',
			wasPrice
				? `<div class="wholesale-line__price wholesale-line__price--sale">
						<s>${formatMoney(wasPrice * qty)}</s>
						${formatMoney(price * qty)}
					</div>`
				: `<div class="wholesale-line__price">${formatMoney(price * qty)}</div>`
		);
	});

	/* Update vodka promo state and render promo section */
	updateVodkaPromoState(dom);
	renderPromoSection(dom);

	/* subtotal + btn / empty message */
	const subtotal = calcSubtotal();

	/* Flash sale savings row */
	const savings = Object.values(cart).reduce(
		(a, { qty, price, wasPrice }) => a + (wasPrice ? (wasPrice - price) * qty : 0), 0
	);
	const savingsRow = $('#flashSaleSavings', dom.form);
	if (savingsRow) {
		const span = $('span', savingsRow);
		if (span) span.textContent = `-${formatMoney(savings)}`;
		savingsRow.classList.toggle('hide', savings <= 0);
	}

	if (subtotal) {
		$('span', dom.subtotal).textContent = formatMoney(subtotal);
		show(dom.subtotal);
		show(dom.summmaryFooter);
		if (dom.headerSubtotal) {
			dom.headerSubtotal.textContent = formatMoney(subtotal);
			show(dom.headerSubtotal);
		}
		dom.submit.disabled = false;
		hide(dom.message);
	} else {
		hide(dom.summmaryFooter);
		if (dom.headerSubtotal) hide(dom.headerSubtotal);
		dom.submit.disabled = true;
		show(dom.message);
	}

	updateActiveRows(dom.table);

	const totalItems = Object.values(cart).reduce((t, { qty }) => t + qty, 0);
	if (dom.count) {
		dom.count.textContent = `(${totalItems})`;
		dom.count.classList.toggle('hide', totalItems === 0);
	}

	const orderLines = Object.values(cart).map(({ qty, title, pkgSingle, pkgPlural, wasPrice }) =>
		`${qty} x ${title} (${qty === 1 ? pkgSingle : pkgPlural})${wasPrice ? ' — FLASH SALE 25% OFF' : ''}`
	);

	/* Put the nicely-formatted list into the hidden textarea */
	dom.orderDetails.value = orderLines.join('\n');

	/* force liquor-licence logic after every cart update */
	enforceAlcoholRequirement(dom.form);

	/* keep the bottom fade as the last child so it sticks past the new lines,
	   then recheck whether the summary overflows */
	const bottomFade = $('.wholesale-order__scroll-fade--bottom', dom.summaryBody);
	if (bottomFade) dom.summaryBody.appendChild(bottomFade);
	updateScrollHints(dom);
}

function getPkgLabels(row) {
	// Try to read from the input's data-attributes first
	const input = row.querySelector('.js-qty__input');
	let single = input?.dataset.packageTypeSingle;
	let plural = input?.dataset.packageTypePlural;

	// Fallback based on row's category if the attributes are missing
	if (!single || !plural) {
		const cat = (row.dataset.category || '').toLowerCase();
		if (cat === 'beer' || cat === 'sodas' || cat === 'botanical-brew') {
			single = 'case'; plural = 'cases';
		} else if (cat === 'merchandise') {
			single = 'pump'; plural = 'pumps';
		} else {
			single = 'box'; plural = 'boxes';
		}
	}
	return { single, plural };
}

function setQty(id, qty, row) {
	const title = $('td div', row).childNodes[0].textContent.trim();
	const img = $('img', row)?.getAttribute('src') || '';
	const unit = moneyToNumber($('td:nth-child(2)', row).childNodes[0].textContent);

	/* Flash sale: if the price cell renders a struck-through original
	   (<s class="price-was">), keep it so the summary can show the saving. */
	const wasEl = $('td:nth-child(2) .price-was', row);
	const wasPrice = wasEl ? moneyToNumber(wasEl.textContent) : null;

	const { single: pkgSingle, plural: pkgPlural } = getPkgLabels(row);
	const category = (row.dataset.category || '').toLowerCase();

	if (qty <= 0) {
		delete cart[id];
	} else {
		cart[id] = { qty, title, img, price: unit, wasPrice, pkgSingle, pkgPlural, category };
	}
}

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */
function handleQtyClick(e, dom) {
	const btn = e.target.closest(ELEM.qtyBtn);
	if (!btn) return;

	const wrap = btn.closest(ELEM.qtyWrap);
	const input = $(ELEM.qtyInput, wrap);
	const row = btn.closest('tr');
	let qty = Number(input.value) || 0;

	qty += btn.classList.contains('js-qty__btn--plus') ? 1 : -1;
	qty = Math.max(qty, 0);
	input.value = qty;

	setQty(input.dataset.variantId, qty, row);
	renderSummary(dom);
}

function handleQtyManual(e, dom) {
	const input = e.target.closest(ELEM.qtyInput);
	if (!input) return;

	let qty = parseInt(input.value, 10);
	if (isNaN(qty) || qty < 0) qty = 0;
	input.value = qty;

	const row = input.closest('tr');
	setQty(input.dataset.variantId, qty, row);
	renderSummary(dom);
}

function handleLineRemove(e, dom) {
	const btn = e.target.closest(ELEM.lineRemove);
	if (!btn) return;

	const id = btn.closest('.wholesale-line').dataset.variantId;
	delete cart[id];

	const input = $(`${ELEM.qtyInput}[data-variant-id="${id}"]`, dom.table);
	if (input) input.value = 0;

	renderSummary(dom);
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach wholesaleOrder behaviour to `document`.
 *
 * @return {() => void}
 */
function initWholesaleOrder() {
	const form = $(ELEM.form);
	if (!form) return () => { };

	/* Cache key nodes once */
	const dom = {
		form,
		table: $(ELEM.table, form),
		summary: $(ELEM.summary, form),
		summaryBody: $(ELEM.summaryBody, form),
		subtotal: $(ELEM.subtotal, form),
		headerSubtotal: $(ELEM.headerSubtotal, form),
		summmaryFooter: $(ELEM.summmaryFooter, form),
		submit: $(ELEM.submit, form),
		disclaimer: $(ELEM.disclaimer, form),
		message: $(ELEM.message, form),
		orderDetails: $(ELEM.orderDetails, form),
		count: $(ELEM.count, form),
	};

	/* Initial blank state */
	hide(dom.summmaryFooter);

	/* Filter controls ---------------------------------------------- */
	$(ELEM.filter, form)?.addEventListener('change', (e) => applyFilter(dom.table, e.target.value));
	$(ELEM.clearFilter, form)?.addEventListener('click', () => applyFilter(dom.table, 'all'));

	/* Reset order --------------------------------------------------- */
	$(ELEM.resetOrder, form)?.addEventListener('click', () => {
		cart = Object.create(null);
		$$(ELEM.qtyInput, dom.table).forEach((i) => (i.value = 0));
		form.reset(); // only contact fields
		toggleAlcoholFields(form); // reset UI
		enforceAlcoholRequirement(form); // (re)sync checkbox

		// Reset vodka promo
		vodkaPromoState.vodkaQuantity = 0;
		vodkaPromoState.selectedCordials = {};
		const freeCordialField = document.getElementById('FreeCordialDetails');
		if (freeCordialField) freeCordialField.value = '';

		renderSummary(dom);
		applyFilter(dom.table, 'all');
	});

	/* Qty / remove delegation --------------------------------------- */
	dom.table.addEventListener('click', (e) => handleQtyClick(e, dom));
	dom.table.addEventListener('input', (e) => handleQtyManual(e, dom));
	dom.summary.addEventListener('click', (e) => handleLineRemove(e, dom));

	/* Scroll affordance ---------------------------------------------- */
	dom.summaryBody?.addEventListener('scroll', () => updateScrollHints(dom), { passive: true });
	window.addEventListener('resize', () => updateScrollHints(dom));
	/* summary is a <details> on mobile; its height changes when toggled */
	dom.summary.addEventListener('toggle', () => updateScrollHints(dom));
	$('.wholesale-order__scroll-hint', form)?.addEventListener('click', () => {
		dom.summaryBody?.scrollBy({
			top: dom.summaryBody.clientHeight * 0.8,
			behavior: 'smooth',
		});
	});

	/* Alcohol licence ------------------------------------------------------ */
	toggleAlcoholFields(form); // initial sync
	form.addEventListener('change', (e) => {
		if (e.target.matches(ELEM.alcoholCheckbox)) toggleAlcoholFields(form);
	});

	/* Cordial modal event delegation */
	document.addEventListener('click', (e) => {
		// Close modal
		if (e.target.matches('.cordial-modal__close') || e.target.matches('.cordial-modal__overlay')) {
			closeCordialModal();
		}

		// Confirm selection
		if (e.target.matches('.cordial-modal__confirm')) {
			confirmCordialSelection(dom);
		}

		// Increase cordial quantity
		if (e.target.closest('.js-cordial-plus')) {
			const input = e.target.closest('.cordial-qty').querySelector('.js-cordial-input');
			if (input) {
				updateCordialQuantity(input.dataset.cordialId, 1, vodkaPromoState.vodkaQuantity);
			}
		}

		// Decrease cordial quantity
		if (e.target.closest('.js-cordial-minus')) {
			const input = e.target.closest('.cordial-qty').querySelector('.js-cordial-input');
			if (input) {
				updateCordialQuantity(input.dataset.cordialId, -1, vodkaPromoState.vodkaQuantity);
			}
		}
	});

	/* Free Cordials Validation Modal Functions */
	function showValidationModal(remaining, selected, total) {
		const modal = document.getElementById('cordialValidationModal');
		if (!modal) return;

		// Update counts in the modal
		const remainingSpan = document.getElementById('validationRemainingCount');
		const selectedSpan = document.getElementById('validationSelectedCount');
		const totalSpan = document.getElementById('validationTotalCount');
		const pluralS = document.getElementById('validationPluralS');
		const totalPluralS = document.getElementById('validationTotalPluralS');

		if (remainingSpan) remainingSpan.textContent = remaining;
		if (selectedSpan) selectedSpan.textContent = selected;
		if (totalSpan) totalSpan.textContent = total;
		if (pluralS) pluralS.textContent = remaining === 1 ? '' : 's';
		if (totalPluralS) totalPluralS.textContent = total === 1 ? '' : 's';

		// Show modal
		modal.classList.remove('hide');
		document.body.style.overflow = 'hidden';
	}

	function closeValidationModal() {
		const modal = document.getElementById('cordialValidationModal');
		if (!modal) return;

		modal.classList.add('hide');
		document.body.style.overflow = '';
	}

	/* Form submission validation for free cordials */
	form.addEventListener('submit', (e) => {
		// Only validate if vodka promo is active
		if (vodkaPromoState.vodkaQuantity === 0) return true;

		// Calculate total selected free cordials
		const totalSelectedCordials = Object.values(vodkaPromoState.selectedCordials)
			.reduce((sum, qty) => sum + qty, 0);

		// If user hasn't selected all their free cordials, prevent submission
		const remainingCordials = vodkaPromoState.vodkaQuantity - totalSelectedCordials;
		if (remainingCordials > 0) {
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation();
			showValidationModal(remainingCordials, totalSelectedCordials, vodkaPromoState.vodkaQuantity);
			return false;
		}

		return true;
	}, true); // Use capture phase to ensure this runs first

	/* Validation modal event handlers */
	document.addEventListener('click', (e) => {
		// Close validation modal
		if (e.target.closest('.cordial-modal--validation .cordial-modal__close') ||
			(e.target.matches('.cordial-modal--validation .cordial-modal__overlay'))) {
			closeValidationModal();
		}

		// Select now button - close validation modal and open selection modal
		if (e.target.matches('.cordial-modal__select-now')) {
			closeValidationModal();
			setTimeout(() => {
				openCordialModal(vodkaPromoState.vodkaQuantity);
			}, 100);
		}
	});

	/* First render */
	renderSummary(dom);
}

initWholesaleOrder();
